import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add,
  AutoStories,
  CheckCircle,
  DeleteOutline,
  FactCheck,
  OpenInNew,
  Save,
  SmartToy,
  UploadFile,
} from '@mui/icons-material';

import {
  Artifact,
  platformApi,
  Project,
  ProjectInterviewSession,
  Transcript,
  TranscriptVersion,
} from '../../services/platformApi';
import { formatTranslatedLabel } from '../../i18n';
import { useLanguage } from '../../LanguageContext';
import { getStatusColor, languageOptions } from '../projectConstants';
import { formatFileSize } from './workspaceConfig';
import { AiInterviewPanel } from './AiInterviewPanel';
import { SyncedTranscript } from './SyncedTranscript';

interface InterviewStudioTabProps {
  project: Project | null;
}

type InterviewMethod = 'upload_audio' | 'upload_video' | 'zoom_video' | 'ai_agent';

const methodOptions: Array<{ value: InterviewMethod; label: string }> = [
  { value: 'upload_audio', label: 'Upload audio' },
  { value: 'upload_video', label: 'Upload video' },
  { value: 'zoom_video', label: 'Zoom video' },
  { value: 'ai_agent', label: 'AI interviewer' },
];

const genderOptions = ['unspecified', 'female', 'male'] as const;

const isZoomMeetingLink = (value: string): boolean => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' && (parsed.hostname === 'zoom.us' || parsed.hostname.endsWith('.zoom.us'));
  } catch {
    return false;
  }
};

const textDirectionForLanguage = (language: string, project: Project): 'ltr' | 'rtl' => {
  if (language === 'he') {
    return 'rtl';
  }
  if (language === project.primary_language_code && project.text_direction === 'rtl') {
    return 'rtl';
  }
  return 'ltr';
};

const formatDate = (value: string | null | undefined, locale: string, notScheduled: string) =>
  value ? new Date(value).toLocaleString(locale) : notScheduled;

export const InterviewStudioTab: React.FC<InterviewStudioTabProps> = ({ project }) => {
  const { locale, t } = useLanguage();
  const [sessions, setSessions] = useState<ProjectInterviewSession[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [transcripts, setTranscripts] = useState<Transcript[]>([]);
  const [versions, setVersions] = useState<TranscriptVersion[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null);
  const [selectedRecordingId, setSelectedRecordingId] = useState<number | null>(null);
  const [activeTranscriptId, setActiveTranscriptId] = useState<number | null>(null);
  const [zoomDrafts, setZoomDrafts] = useState<Record<number, string>>({});
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [recordingDialogOpen, setRecordingDialogOpen] = useState(false);
  const [transcriptUploadDialogOpen, setTranscriptUploadDialogOpen] = useState(false);
  const [aiInterviewDialogOpen, setAiInterviewDialogOpen] = useState(false);
  const [aiIntervieweeGender, setAiIntervieweeGender] = useState<string>('unspecified');
  const [aiGoal, setAiGoal] = useState('');
  const [aiOutline, setAiOutline] = useState('');
  const [recordingFile, setRecordingFile] = useState<File | null>(null);
  const [transcriptUploadFile, setTranscriptUploadFile] = useState<File | null>(null);
  const [transcriptUploadText, setTranscriptUploadText] = useState('');
  const [transcriptUploadSessionId, setTranscriptUploadSessionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sessionForm, setSessionForm] = useState({
    title: '',
    method: 'upload_audio' as InterviewMethod,
    interviewerName: '',
    intervieweeName: '',
    scheduledAt: '',
    maxDuration: '60',
    language: project?.primary_language_code || 'en',
    zoomLink: '',
    notes: '',
  });
  const [transcriptDraft, setTranscriptDraft] = useState({
    text: '',
    language: project?.primary_language_code || 'en',
    textDirection: project?.text_direction || 'ltr',
    changeNote: '',
  });

  const loadStudio = useCallback(async () => {
    if (!project) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [sessionData, artifactData, transcriptData] = await Promise.all([
        platformApi.listProjectInterviewSessions(project.id),
        platformApi.listProjectArtifacts(project.id),
        platformApi.listProjectTranscripts(project.id),
      ]);
      setSessions(sessionData);
      setArtifacts(artifactData);
      setTranscripts(transcriptData);
      setZoomDrafts(Object.fromEntries(sessionData.map((session) => [session.id, session.meeting_link || ''])));
      setActiveSessionId((current) =>
        current && sessionData.some((session) => session.id === current) ? current : sessionData[0]?.id ?? null
      );
    } catch {
      setError(t('couldNotLoadInterviewStudio'));
    } finally {
      setLoading(false);
    }
  }, [project, t]);

  useEffect(() => {
    loadStudio();
  }, [loadStudio]);

  useEffect(() => {
    if (project) {
      setSessionForm((current) => ({
        ...current,
        language: current.language || project.primary_language_code || 'en',
      }));
      setTranscriptDraft((current) => ({
        ...current,
        language: current.language || project.primary_language_code || 'en',
        textDirection: current.textDirection || project.text_direction || 'ltr',
      }));
    }
  }, [project]);

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) || sessions[0],
    [activeSessionId, sessions]
  );

  const sessionRecordings = useMemo(() => {
    if (!activeSession) {
      return [];
    }
    return artifacts.filter(
      (artifact) =>
        artifact.interview_session_id === activeSession.id &&
        (artifact.artifact_type === 'audio' || artifact.artifact_type === 'video')
    );
  }, [activeSession, artifacts]);

  const sessionTranscripts = useMemo(() => {
    if (!activeSession) {
      return [];
    }
    return transcripts.filter((transcript) => transcript.interview_session_id === activeSession.id);
  }, [activeSession, transcripts]);

  const selectedRecording = useMemo(
    () => sessionRecordings.find((artifact) => artifact.id === selectedRecordingId) || sessionRecordings[0],
    [selectedRecordingId, sessionRecordings]
  );

  const activeTranscript = useMemo(
    () =>
      transcripts.find((transcript) => transcript.id === activeTranscriptId) ||
      sessionTranscripts[0],
    [activeTranscriptId, sessionTranscripts, transcripts]
  );

  useEffect(() => {
    if (selectedRecording && selectedRecording.id !== selectedRecordingId) {
      setSelectedRecordingId(selectedRecording.id);
    }
    if (!selectedRecording && selectedRecordingId) {
      setSelectedRecordingId(null);
    }
  }, [selectedRecording, selectedRecordingId]);

  useEffect(() => {
    if (activeTranscript && activeTranscript.id !== activeTranscriptId) {
      setActiveTranscriptId(activeTranscript.id);
    }
    if (!activeTranscript && activeTranscriptId) {
      setActiveTranscriptId(null);
    }
  }, [activeTranscript, activeTranscriptId]);

  useEffect(() => {
    if (!activeTranscript) {
      setTranscriptDraft((current) => ({
        ...current,
        text: '',
        textDirection: project?.text_direction || 'ltr',
        changeNote: '',
      }));
      setVersions([]);
      return;
    }
    if (activeTranscript.interview_session_id !== activeSessionId) {
      setActiveSessionId(activeTranscript.interview_session_id);
    }
    if (activeTranscript.source_artifact_id && activeTranscript.source_artifact_id !== selectedRecordingId) {
      setSelectedRecordingId(activeTranscript.source_artifact_id);
    }
    setTranscriptDraft({
      text: activeTranscript.text,
      language: activeTranscript.language || project?.primary_language_code || 'en',
      textDirection:
        (activeTranscript.metadata_json?.text_direction as string | undefined) ||
        (project ? textDirectionForLanguage(activeTranscript.language, project) : 'ltr'),
      changeNote: '',
    });
    if (project) {
      platformApi
        .listProjectTranscriptVersions(project.id, activeTranscript.id)
        .then(setVersions)
        .catch(() => setVersions([]));
    }
  }, [activeSessionId, activeTranscript, project, selectedRecordingId]);

  const createSession = async (): Promise<boolean> => {
    if (!project || !sessionForm.title.trim() || !sessionForm.intervieweeName.trim()) {
      setError(t('interviewTitleAndIntervieweeRequired'));
      return false;
    }
    if (sessionForm.method === 'zoom_video' && sessionForm.zoomLink.trim() && !isZoomMeetingLink(sessionForm.zoomLink.trim())) {
      setError(t('zoomMeetingLinkMustBeHttps'));
      return false;
    }

    setSaving(true);
    setError(null);
    try {
      const created = await platformApi.createProjectInterviewSession(project.id, {
        title: sessionForm.title.trim(),
        interview_type: sessionForm.method === 'upload_audio' ? 'audio' : 'video',
        interviewer_name: sessionForm.interviewerName.trim() || t('interviewer'),
        interviewee_name: sessionForm.intervieweeName.trim(),
        scheduled_at: sessionForm.scheduledAt ? new Date(sessionForm.scheduledAt).toISOString() : undefined,
        max_duration: Number(sessionForm.maxDuration) || 60,
        notes: sessionForm.notes.trim() || undefined,
        meeting_link: sessionForm.zoomLink.trim() || undefined,
      });
      setSessionForm({
        title: '',
        method: 'upload_audio',
        interviewerName: '',
        intervieweeName: '',
        scheduledAt: '',
        maxDuration: '60',
        language: project.primary_language_code || 'en',
        zoomLink: '',
        notes: '',
      });
      await loadStudio();
      setActiveSessionId(created.id);
      return true;
    } catch {
      setError(t('couldNotCreateInterviewSession'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveZoomLink = async () => {
    if (!project || !activeSession) {
      return;
    }
    const meetingLink = (zoomDrafts[activeSession.id] || '').trim();
    if (!meetingLink || !isZoomMeetingLink(meetingLink)) {
      setError(t('zoomMeetingLinkMustBeHttps'));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await platformApi.configureInterviewStudioZoomSession(project.id, activeSession.id, {
        meeting_link: meetingLink,
        status: 'ready',
      });
      await loadStudio();
    } catch {
      setError(t('couldNotSaveZoomMeetingLink'));
    } finally {
      setSaving(false);
    }
  };

  const uploadRecording = async (): Promise<boolean> => {
    if (!project || !activeSession || !recordingFile) {
      setError(t('chooseSessionAndRecordingFileFirst'));
      return false;
    }
    const artifactType = recordingFile.type.startsWith('video/') ? 'video' : 'audio';
    setSaving(true);
    setError(null);
    try {
      const artifact = await platformApi.uploadProjectArtifact(project.id, {
        artifact_type: artifactType,
        file: recordingFile,
        title: recordingFile.name,
        description: t('interviewStudioRecordingFor').replace('{title}', activeSession.interview_title),
        interview_session_id: activeSession.id,
      });
      setRecordingFile(null);
      await loadStudio();
      setSelectedRecordingId(artifact.id);
      return true;
    } catch {
      setError(t('couldNotUploadRecording'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const generateTranscript = async () => {
    if (!project || !activeSession || !selectedRecording) {
      setError(t('chooseRecordingBeforeGeneratingTranscript'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const transcript = await platformApi.generateProjectTranscript(project.id, {
        interview_session_id: activeSession.id,
        source_artifact_id: selectedRecording.id,
        language: transcriptDraft.language || project.primary_language_code || 'en',
      });
      await loadStudio();
      setActiveTranscriptId(transcript.id);
    } catch {
      setError(t('couldNotGenerateTranscript'));
    } finally {
      setSaving(false);
    }
  };

  const handleTranscriptUploadFile = async (file: File | null) => {
    setTranscriptUploadFile(file);
    if (!file) {
      return;
    }
    try {
      const text = await file.text();
      setTranscriptUploadText(text);
    } catch {
      setError(t('couldNotReadTranscriptFile'));
    }
  };

  const uploadTranscript = async (): Promise<boolean> => {
    if (!project || !transcriptUploadSessionId || !transcriptUploadText.trim()) {
      setError(t('transcriptSessionAndTextRequired'));
      return false;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await platformApi.createProjectTranscript(project.id, {
        interview_session_id: Number(transcriptUploadSessionId),
        text: transcriptUploadText,
        language: transcriptDraft.language || project.primary_language_code || 'en',
        status: 'draft',
        metadata_json: {
          interview_studio: true,
          source: 'transcript_upload',
          file_name: transcriptUploadFile?.name,
          text_direction: transcriptDraft.textDirection || project.text_direction || 'ltr',
        },
      });
      setTranscriptUploadFile(null);
      setTranscriptUploadText('');
      await loadStudio();
      setActiveSessionId(saved.interview_session_id);
      setActiveTranscriptId(saved.id);
      return true;
    } catch {
      setError(t('couldNotSaveTranscript'));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const refineTranscript = async () => {
    if (!project || !activeTranscript || !transcriptDraft.text.trim()) {
      setError(t('transcriptTextRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const refined = await platformApi.refineProjectTranscript(project.id, activeTranscript.id, {
        text: transcriptDraft.text,
        language: transcriptDraft.language,
        instructions: transcriptDraft.changeNote.trim() || t('aiTranscriptRefinement'),
      });
      await loadStudio();
      setActiveTranscriptId(refined.id);
    } catch {
      setError(t('couldNotRefineTranscript'));
    } finally {
      setSaving(false);
    }
  };

  const saveTranscript = async () => {
    if (!project || !activeSession || !transcriptDraft.text.trim()) {
      setError(t('transcriptTextRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const metadata = {
        ...(activeTranscript?.metadata_json || {}),
        interview_studio: true,
        source_recording_id: selectedRecording?.id,
        text_direction: transcriptDraft.textDirection,
      };
      const saved = activeTranscript
        ? await platformApi.updateProjectTranscript(project.id, activeTranscript.id, {
            text: transcriptDraft.text,
            language: transcriptDraft.language,
            status: activeTranscript.status === 'approved' ? 'corrected' : activeTranscript.status || 'corrected',
            change_note: transcriptDraft.changeNote.trim() || t('manualInterviewStudioEdit'),
            metadata_json: metadata,
          })
        : await platformApi.createProjectTranscript(project.id, {
            interview_session_id: activeSession.id,
            source_artifact_id: selectedRecording?.id,
            text: transcriptDraft.text,
            language: transcriptDraft.language,
            status: 'draft',
            metadata_json: metadata,
          });
      await loadStudio();
      setActiveTranscriptId(saved.id);
    } catch {
      setError(t('couldNotSaveTranscript'));
    } finally {
      setSaving(false);
    }
  };

  const approveTranscript = async () => {
    if (!project || !activeTranscript) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const approved = await platformApi.approveProjectTranscript(project.id, activeTranscript.id);
      await loadStudio();
      setActiveTranscriptId(approved.id);
    } catch {
      setError(t('couldNotApproveTranscript'));
    } finally {
      setSaving(false);
    }
  };

  const deleteTranscript = async () => {
    if (!project || !activeTranscript) {
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('confirmDeleteTranscript'))) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await platformApi.deleteProjectTranscript(project.id, activeTranscript.id);
      setActiveTranscriptId(null);
      await loadStudio();
    } catch {
      setError(t('couldNotDeleteTranscript'));
    } finally {
      setSaving(false);
    }
  };

  const openAiInterviewDialog = () => {
    if (!activeSession) {
      return;
    }
    setAiIntervieweeGender('unspecified');
    setAiGoal('');
    setAiOutline('');
    setAiInterviewDialogOpen(true);
  };

  const handleAiInterviewCompleted = async (result: {
    transcript_id?: number | null;
    artifact_id?: number | null;
  }) => {
    await loadStudio();
    if (result.transcript_id) {
      setActiveTranscriptId(result.transcript_id);
    }
  };

  if (!project) {
    return (
      <Alert severity="info">
        {t('loadProjectToUseInterviewStudio')}
      </Alert>
    );
  }

  const transcriptDirection = transcriptDraft.textDirection || textDirectionForLanguage(transcriptDraft.language, project);
  const recordingUrl = selectedRecording
    ? selectedRecording.external_url || platformApi.getProjectArtifactDownloadUrl(project.id, selectedRecording.id)
    : '';

  const openScheduleDialog = () => {
    setScheduleDialogOpen(true);
  };

  const openRecordingDialog = (sessionId?: number) => {
    if (sessionId) {
      setActiveSessionId(sessionId);
    }
    setRecordingDialogOpen(true);
  };

  const openTranscriptUploadDialog = () => {
    setTranscriptUploadSessionId(activeSession?.id.toString() || sessions[0]?.id.toString() || '');
    setTranscriptUploadDialogOpen(true);
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
        <Box>
          <Typography variant="h5" component="h2" fontWeight={700}>
            {t('interviewStudio')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('interviewStudioDescription')}
          </Typography>
        </Box>
        <Button onClick={loadStudio} disabled={loading || saving}>
          {t('refresh')}
        </Button>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Dialog open={scheduleDialogOpen} onClose={() => !saving && setScheduleDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('scheduleInterview')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} alignItems="center" sx={{ pt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('interviewTitle')}
                value={sessionForm.title}
                onChange={(event) => setSessionForm({ ...sessionForm, title: event.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="interview-studio-method-label">{t('method')}</InputLabel>
                <Select
                  labelId="interview-studio-method-label"
                  label={t('method')}
                  value={sessionForm.method}
                  onChange={(event) => setSessionForm({ ...sessionForm, method: event.target.value as InterviewMethod })}
                >
                  {methodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {formatTranslatedLabel(t, option.value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('interviewer')}
                value={sessionForm.interviewerName}
                onChange={(event) => setSessionForm({ ...sessionForm, interviewerName: event.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('interviewee')}
                value={sessionForm.intervieweeName}
                onChange={(event) => setSessionForm({ ...sessionForm, intervieweeName: event.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t('scheduled')}
                type="datetime-local"
                value={sessionForm.scheduledAt}
                onChange={(event) => setSessionForm({ ...sessionForm, scheduledAt: event.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label={t('minutes')}
                type="number"
                value={sessionForm.maxDuration}
                onChange={(event) => setSessionForm({ ...sessionForm, maxDuration: event.target.value })}
                fullWidth
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="interview-studio-language-label">{t('language')}</InputLabel>
                <Select
                  labelId="interview-studio-language-label"
                  label={t('language')}
                  value={sessionForm.language}
                  onChange={(event) => {
                    const language = event.target.value;
                    const textDirection = textDirectionForLanguage(language, project);
                    setSessionForm({ ...sessionForm, language });
                    setTranscriptDraft((current) => ({ ...current, language, textDirection }));
                  }}
                >
                  {languageOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('zoomMeetingLink')}
                value={sessionForm.zoomLink}
                onChange={(event) => setSessionForm({ ...sessionForm, method: 'zoom_video', zoomLink: event.target.value })}
                placeholder="https://your-company.zoom.us/j/..."
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('notes')}
                value={sessionForm.notes}
                onChange={(event) => setSessionForm({ ...sessionForm, notes: event.target.value })}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={async () => {
              if (await createSession()) {
                setScheduleDialogOpen(false);
              }
            }}
            disabled={saving}
          >
            {t('scheduleInterview')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={recordingDialogOpen} onClose={() => !saving && setRecordingDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('uploadInterviewRecording')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth disabled={sessions.length === 0}>
              <InputLabel id="recording-dialog-session-label">{t('interviewSession')}</InputLabel>
              <Select
                labelId="recording-dialog-session-label"
                label={t('interviewSession')}
                value={activeSession?.id.toString() || ''}
                onChange={(event) => setActiveSessionId(Number(event.target.value))}
              >
                {sessions.map((session) => (
                  <MenuItem key={session.id} value={session.id.toString()}>
                    {session.interview_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button component="label" variant="outlined" startIcon={<UploadFile />} disabled={!activeSession || saving}>
              {t('chooseRecording')}
              <input hidden type="file" accept="audio/*,video/*" onChange={(event) => setRecordingFile(event.target.files?.[0] || null)} />
            </Button>
            {recordingFile && (
              <Typography variant="body2">
                {recordingFile.name} · {formatFileSize(recordingFile.size)}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordingDialogOpen(false)} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFile />}
            onClick={async () => {
              if (await uploadRecording()) {
                setRecordingDialogOpen(false);
              }
            }}
            disabled={!activeSession || !recordingFile || saving}
          >
            {t('uploadRecording')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transcriptUploadDialogOpen} onClose={() => !saving && setTranscriptUploadDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{t('uploadTranscript')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth disabled={sessions.length === 0}>
              <InputLabel id="transcript-upload-session-label">{t('interviewSession')}</InputLabel>
              <Select
                labelId="transcript-upload-session-label"
                label={t('interviewSession')}
                value={transcriptUploadSessionId}
                onChange={(event) => setTranscriptUploadSessionId(event.target.value)}
              >
                {sessions.map((session) => (
                  <MenuItem key={session.id} value={session.id.toString()}>
                    {session.interview_title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button component="label" variant="outlined" startIcon={<UploadFile />} disabled={saving}>
              {t('chooseTranscriptFile')}
              <input hidden type="file" accept=".txt,.vtt,.srt,text/*" onChange={(event) => handleTranscriptUploadFile(event.target.files?.[0] || null)} />
            </Button>
            {transcriptUploadFile && (
              <Typography variant="body2">
                {transcriptUploadFile.name} · {formatFileSize(transcriptUploadFile.size)}
              </Typography>
            )}
            <TextField
              label={t('transcriptText')}
              value={transcriptUploadText}
              onChange={(event) => setTranscriptUploadText(event.target.value)}
              multiline
              minRows={8}
              fullWidth
              inputProps={{ dir: transcriptDirection }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranscriptUploadDialogOpen(false)} disabled={saving}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadFile />}
            onClick={async () => {
              if (await uploadTranscript()) {
                setTranscriptUploadDialogOpen(false);
              }
            }}
            disabled={!transcriptUploadSessionId || !transcriptUploadText.trim() || saving}
          >
            {t('uploadTranscript')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiInterviewDialogOpen}
        onClose={() => setAiInterviewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {t('conductAiInterview')}
          {activeSession ? ` — ${activeSession.interviewee_name}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="ai-interview-language-label">{t('language')}</InputLabel>
              <Select
                labelId="ai-interview-language-label"
                label={t('language')}
                value={transcriptDraft.language}
                onChange={(event) =>
                  setTranscriptDraft((current) => ({ ...current, language: event.target.value }))
                }
              >
                {languageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="ai-interview-gender-label">{t('intervieweeGender')}</InputLabel>
              <Select
                labelId="ai-interview-gender-label"
                label={t('intervieweeGender')}
                value={aiIntervieweeGender}
                onChange={(event) => setAiIntervieweeGender(event.target.value)}
              >
                {genderOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {formatTranslatedLabel(t, option)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('interviewGoal')}
              value={aiGoal}
              onChange={(event) => setAiGoal(event.target.value)}
              fullWidth
            />
            <TextField
              label={t('interviewOutline')}
              value={aiOutline}
              onChange={(event) => setAiOutline(event.target.value)}
              placeholder={t('interviewOutlinePlaceholder')}
              multiline
              minRows={3}
              fullWidth
            />
            {project && activeSession && (
              <AiInterviewPanel
                projectId={project.id}
                sessionId={activeSession.id}
                intervieweeName={activeSession.interviewee_name}
                config={{
                  language: transcriptDraft.language,
                  interviewee_gender: aiIntervieweeGender,
                  goal: aiGoal.trim() || undefined,
                  outline: aiOutline.trim() || undefined,
                }}
                onCompleted={async (result) => {
                  await handleAiInterviewCompleted(result);
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAiInterviewDialogOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {t('interviewSessions')}
              </Typography>
              <Button variant="contained" startIcon={<Add />} onClick={openScheduleDialog} disabled={saving}>
                {t('scheduleInterview')}
              </Button>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('interviewTitle')}</TableCell>
                    <TableCell>{t('interviewee')}</TableCell>
                    <TableCell>{t('scheduled')}</TableCell>
                    <TableCell align="right">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sessions.map((session) => (
                    <TableRow key={session.id} hover selected={activeSession?.id === session.id}>
                      <TableCell>
                        <Typography fontWeight={700}>{session.interview_title}</Typography>
                        <Chip size="small" label={formatTranslatedLabel(t, session.status)} color={getStatusColor(session.status)} />
                      </TableCell>
                      <TableCell>{session.interviewee_name}</TableCell>
                      <TableCell>{formatDate(session.scheduled_at, locale, t('notScheduled'))}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button size="small" onClick={() => setActiveSessionId(session.id)}>
                            {t('select')}
                          </Button>
                          <Button size="small" startIcon={<UploadFile />} onClick={() => openRecordingDialog(session.id)}>
                            {t('uploadRecording')}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                  {sessions.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary" sx={{ py: 4 }} align="center">
                          {t('noSessionsYet')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>
                {t('transcripts')}
              </Typography>
              <Button variant="contained" startIcon={<UploadFile />} onClick={openTranscriptUploadDialog} disabled={sessions.length === 0 || saving}>
                {t('uploadTranscript')}
              </Button>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('transcript')}</TableCell>
                    <TableCell>{t('interviewSession')}</TableCell>
                    <TableCell>{t('status')}</TableCell>
                    <TableCell align="right">{t('actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transcripts.map((transcript) => {
                    const transcriptSession = sessions.find((session) => session.id === transcript.interview_session_id);
                    return (
                      <TableRow key={transcript.id} hover selected={activeTranscript?.id === transcript.id}>
                        <TableCell>
                          <Typography fontWeight={700}>{t('transcript')} #{transcript.id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {transcript.created_at ? new Date(transcript.created_at).toLocaleString(locale) : t('notRecorded')}
                          </Typography>
                        </TableCell>
                        <TableCell>{transcriptSession?.interview_title || t('interviewSession')}</TableCell>
                        <TableCell>
                          <Chip size="small" label={formatTranslatedLabel(t, transcript.status)} color={getStatusColor(transcript.status)} />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            onClick={() => {
                              setActiveSessionId(transcript.interview_session_id);
                              setActiveTranscriptId(transcript.id);
                            }}
                          >
                            {t('edit')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {transcripts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary" sx={{ py: 4 }} align="center">
                          {t('noTranscriptsYet')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {t('transcript')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('transcriptEditorDescription')}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {activeTranscript && (
                <Chip size="small" label={formatTranslatedLabel(t, activeTranscript.status)} color={getStatusColor(activeTranscript.status)} />
              )}
              <Chip size="small" label={`${versions.length || (activeTranscript ? 1 : 0)} ${t('versions')}`} />
            </Stack>
          </Stack>

          {activeSession && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                <Box>
                  <Typography fontWeight={700}>{activeSession.interview_title}</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip size="small" label={formatTranslatedLabel(t, activeSession.interview_type)} />
                    <Chip size="small" label={formatTranslatedLabel(t, activeSession.status)} color={getStatusColor(activeSession.status)} />
                    <Chip size="small" label={`${activeSession.max_duration || 0} ${t('min')}`} />
                  </Stack>
                </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                  <TextField
                    size="small"
                    label={t('zoomLink')}
                    value={zoomDrafts[activeSession.id] ?? activeSession.meeting_link ?? ''}
                    onChange={(event) =>
                      setZoomDrafts((current) => ({
                        ...current,
                        [activeSession.id]: event.target.value,
                      }))
                    }
                  />
                  <Button variant="outlined" startIcon={<Save />} onClick={saveZoomLink} disabled={saving}>
                    {t('saveZoom')}
                  </Button>
                  {activeSession.meeting_link && (
                    <Button
                      variant="contained"
                      startIcon={<OpenInNew />}
                      component="a"
                      href={activeSession.meeting_link}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t('join')}
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<SmartToy />}
                    onClick={openAiInterviewDialog}
                    disabled={saving}
                  >
                    {t('conductAiInterview')}
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          )}

          <Stack spacing={2}>
            <FormControl fullWidth disabled={sessionRecordings.length === 0}>
              <InputLabel id="interview-studio-recording-label">{t('recording')}</InputLabel>
              <Select
                labelId="interview-studio-recording-label"
                label={t('recording')}
                value={selectedRecording?.id.toString() || ''}
                onChange={(event) => setSelectedRecordingId(Number(event.target.value))}
              >
                {sessionRecordings.map((artifact) => (
                  <MenuItem key={artifact.id} value={artifact.id.toString()}>
                    {artifact.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedRecording && (
              <Box>
                <SyncedTranscript
                  projectId={project.id}
                  transcript={activeTranscript || null}
                  recordingUrl={recordingUrl}
                  mediaType={selectedRecording.artifact_type === 'video' ? 'video' : 'audio'}
                  intervieweeName={activeSession?.interviewee_name || t('interviewee')}
                  textDirection={transcriptDirection === 'rtl' ? 'rtl' : 'ltr'}
                />
                <Typography variant="caption" color="text.secondary">
                  {selectedRecording.file_name || selectedRecording.title} · {formatFileSize(selectedRecording.file_size)}
                </Typography>
              </Box>
            )}
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id="interview-studio-transcript-language-label">{t('language')}</InputLabel>
              <Select
                labelId="interview-studio-transcript-language-label"
                label={t('language')}
                value={transcriptDraft.language}
                onChange={(event) => {
                  const language = event.target.value;
                  setTranscriptDraft({
                    ...transcriptDraft,
                    language,
                    textDirection: textDirectionForLanguage(language, project),
                  });
                }}
              >
                {languageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="interview-studio-transcript-direction-label">{t('direction')}</InputLabel>
              <Select
                labelId="interview-studio-transcript-direction-label"
                label={t('direction')}
                value={transcriptDraft.textDirection}
                onChange={(event) => setTranscriptDraft({ ...transcriptDraft, textDirection: event.target.value })}
              >
                <MenuItem value="ltr">{t('ltr')}</MenuItem>
                <MenuItem value="rtl">{t('rtl')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label={t('transcriptText')}
            value={transcriptDraft.text}
            onChange={(event) => setTranscriptDraft({ ...transcriptDraft, text: event.target.value })}
            multiline
            minRows={12}
            fullWidth
            inputProps={{ dir: transcriptDirection }}
          />
          <TextField
            label={t('changeNote')}
            value={transcriptDraft.changeNote}
            onChange={(event) => setTranscriptDraft({ ...transcriptDraft, changeNote: event.target.value })}
            fullWidth
          />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<AutoStories />}
              onClick={generateTranscript}
              disabled={!activeSession || !selectedRecording || saving}
            >
              {t('generateTranscript')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AutoStories />}
              onClick={refineTranscript}
              disabled={!activeTranscript || !transcriptDraft.text.trim() || saving}
            >
              {t('refineTranscript')}
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={saveTranscript} disabled={!activeSession || saving}>
              {t('saveVersion')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CheckCircle />}
              onClick={approveTranscript}
              disabled={!activeTranscript || activeTranscript.status === 'approved' || saving}
            >
              {t('approve')}
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={deleteTranscript}
              disabled={!activeTranscript || saving}
            >
              {t('delete')}
            </Button>
            <Button
              variant="text"
              startIcon={<FactCheck />}
              onClick={() => {
                setActiveTranscriptId(null);
                setTranscriptDraft({
                  text: '',
                  language: project.primary_language_code || 'en',
                  textDirection: project.text_direction || 'ltr',
                  changeNote: '',
                });
              }}
              disabled={saving}
            >
              {t('newManualTranscript')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};
